import { api } from '@/galaxy-cruise/api';
import { loginGuard } from '@/galaxy-cruise/utils';
import { validNickname, integerString } from '@/galaxy-cruise/utils/validation';
import { store } from '@/store';
import { AxiosError } from 'axios';
import { UNPROCESSABLE_ENTITY } from 'http-status-codes';
import { Component, Vue, Watch } from 'vue-property-decorator';

@Component
export default class extends Vue {
  static beforeEnter = loginGuard

  nicknameUnique = true
  validateAlways = false

  get nicknameErrors() {
    return [
      (this.user.nickname === null || this.user.nickname.length === 0) && this.$t('cannot_be_blank'),
      !this.nicknameUnique && this.$t('nickname_already_taken'),
    ].filter((e) => e)
  }

  @Watch('user.nickname', { immediate: true })
  async nicknameChange() {
    this.nicknameUnique = await validNickname(this.user.nickname)
  }

  async submit() {
    if (!this.ok) {
      (this.$refs.dialog as any).shake(() => this.validateAlways = true)
      return
    }

    const user = {
      ...this.user,
      private_information_attributes: {
        country_code: this.country_code,
        gender: this.gender,
        birth_year: this.birth_year,
        relation_to_astronomy: this.relationToAstronomy,
        time_spent_in_astronomy: this.timeSpentInAstronomy,
        way_to_know_this_site:
          this.wayToKnowThisSite === 'other' ? this.wayToKnowThisSiteOther : this.wayToKnowThisSite,
        reasons_for_participation:
          this.reasonsForParticipation.indexOf('other') >= 0 ?
            this.reasonsForParticipation.filter((r) => r !== 'other').concat(this.reasonsForParticipationOther) :
            this.reasonsForParticipation,
      },
    }
    const axios = api.passErrors(UNPROCESSABLE_ENTITY)
    try {
      await axios.put('../api/v1/user', { user })
      this.$router.push('/')
    } catch (e) {
      const error: AxiosError = e
      switch (error.response!.status) {
        case UNPROCESSABLE_ENTITY:
          const errors = error.response!.data
          const message = Object.keys(error.response!.data).map((k) => {
            return errors[k].map((m: string) => `${this.$t(k)}${this.$t(m)}`).join('\n')
          }).join('\n')
          alert(message)
          break
      }
    }
  }

  focus() {
    (this.$refs.nickname as HTMLInputElement).focus()
  }

  get ok() {
    return (
      this.nicknameErrors.length +
      this.countryCodeErrors.length + this.genderErrors.length + this.birthYearErrors.length +
      this.relationToAstronomyErrors.length + this.timeSpentInAstronomyErrors.length +
      this.wayToKnowThisSiteErrors.length + this.wayToKnowThisSiteOtherErrors.length +
      this.reasonsForParticipationErrors.length + this.reasonsForParticipationOtherErrors.length) === 0
  }

  get gc() {
    return store.galaxyCruise
  }

  get user() {
    return this.gc.session!.user
  }

  birth_year: null | number = null
  birthYearString = ''

  @Watch('birthYearString')
  onBirthYearStringChange() {
    const { valid, value } = integerString(this.birthYearString)
    if (valid) {
      this.birth_year = value
    }
  }

  get birthYearErrors() {
    return [
      !this.birthYearString && this.$t('cannot_be_blank'),
      this.birthYearString && !integerString(this.birthYearString).valid && this.$t('enter_digits'),
    ].filter((e) => e)
  }

  country_code: null | number = null

  get countryCodeErrors() {
    return [
      this.country_code === null && this.$t('choose_at_least_one'),
    ].filter((e) => e)
  }

  gender: null | string = null
  get genderErrors() {
    return [
      this.gender === null && this.$t('choose_at_least_one'),
    ].filter((e) => e)
  }

  relationToAstronomy: null | number = null
  get relationToAstronomyErrors() {
    return [
      this.relationToAstronomy === null && this.$t('choose_at_least_one'),
    ].filter((e) => e)
  }

  timeSpentInAstronomy: null | number = null
  get timeSpentInAstronomyErrors() {
    return [
      this.timeSpentInAstronomy === null && this.$t('choose_at_least_one'),
    ].filter((e) => e)
  }

  wayToKnowThisSite: null | string = null
  wayToKnowThisSiteOther = ''

  get wayToKnowThisSiteErrors() {
    return [
      this.wayToKnowThisSite === null && this.$t('choose_at_least_one'),
    ].filter((e) => e)
  }

  get wayToKnowThisSiteOtherErrors() {
    return [
      this.wayToKnowThisSite === 'other' && this.wayToKnowThisSiteOther === '' && this.$t('cannot_be_blank'),
    ].filter((e) => e)
  }

  reasonsForParticipation: string[] = []
  reasonsForParticipationOther = ''

  get reasonsForParticipationErrors() {
    return [
      this.reasonsForParticipation.length === 0 && this.$t('choose_at_least_one'),
    ].filter((e) => e)
  }

  get reasonsForParticipationOtherErrors() {
    return [
      this.reasonsForParticipation.indexOf('other') >= 0 && this.reasonsForParticipationOther === '' && this.$t('cannot_be_blank'),
    ].filter((e) => e)
  }

  get wtktsList() {
    const list = [
      '国立天文台のサイト',
      '国立天文台のSNS',
      '国立天文台のメールマガジン',
      'チラシ',
      'ポスター',
      '友人・知人の紹介',
      'インターネット検索',
      '科学館・プラネタリウム',
    ]
    if (this.$i18n.locale !== 'ja') {
      const i = list.indexOf('国立天文台のメールマガジン')
      if (i >= 0) {
        list.splice(i, 1)
      }
    }
    return list
  }

  get rfpList() {
    return [
      '天文学研究に貢献したいから',
      '天文学を学びたいから',
      'ゲームが面白そうだから',
      '天体画像がきれいだから',
      '天文学・科学教材として使えそうだから',
      '面白い銀河を発見したいから',
      'シチズンサイエンス/市民天文学に興味があるから',
      '知人・友人に勧められたから',
    ]
  }
}
